import React, { useState, useRef, useMemo, useEffect } from 'react'

import EditIcon from '@mui/icons-material/EditOutlined'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import LinkIcon from '@mui/icons-material/Link'
import { Fade } from '@mui/material'
import { TextareaAutosize } from '@mui/base/TextareaAutosize'
import { IconButton } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import Chip from '@mui/material/Chip'
import Box from '@mui/material/Box'
import { useZustand } from '../../hooks/useZustand'
import { useFilterStore } from '../../stores/useFilterStore'
import { useRecalculateCurrentGraph } from '../../hooks/useRecalculateCurrentGraph'
import Tags from './Tags'
import { wrapSelectionInBrackets } from '../../utils/bracketSelection'
import { useSnackbar } from '../Snackbar'

import { sendToAnalytics } from '../../Analytics'
import { UserContext } from '../../UserContext'

const selectorShowSnackbar = state => state.showSnackbar

const SELECTION_BG = 'rgba(35, 131, 226, 0.2)'
const TRANSPARENT_BG = 'transparent'

const Statement = ({
    initialText,
    contextLabel,
    uid,
    highlightedWords,
    grayedOut,
    tags = [],
    isSelected,
    isActiveMatch = false,
    onSelect,
    isAuthoringDisabled,
}) => {
    const theme = useTheme()
    const activeMatchBg = theme.palette.action.selected

    const [selectedTags, setSelectedTags] = useState(tags)
    const [text, setText] = useState(initialText)
    const [isEditable, setIsEditable] = useState(false)
    const [isHovered, setIsHovered] = useState(false)
    const [isLoading, setIsLoading] = useState(false)
    const showSnackbar = useSnackbar(selectorShowSnackbar)
    const recalculateCurrentGraph = useRecalculateCurrentGraph()
    const setSelectedStatements = useFilterStore(
        state => state.setSelectedStatements
    )

    const textareaRef = useRef(null)
    const tagComponentRef = useRef(null)

    const tagsRef = useRef(tags)

    const [linkInText, setLinkInText] = useState('')

    // The server may normalise the saved text (e.g. strip [tags]); follow it.
    useEffect(() => {
        setText(initialText)
    }, [initialText])

    useEffect(() => {
        const areTagsEqual =
            tags.length === tagsRef.current.length &&
            tags.every(tag => tagsRef.current.includes(tag))
        if (!areTagsEqual) {
            tagsRef.current = tags
            setSelectedTags(tags)
        }
    }, [tags])

    const { username } = React.useContext(UserContext)

    useEffect(() => {
        const regex = /https?:\/\/(?:[a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}(?:\/[^\s]*)?/i
        const match = text.match(regex)
        setLinkInText(match ? match[0] : '')
    }, [text])

    // Selecting a word (double-click) or a phrase tags it, as in the add form.
    const handleSelect = e => {
        const { value, selectionStart, selectionEnd } = e.target
        const newValue = wrapSelectionInBrackets(
            value,
            selectionStart,
            selectionEnd
        )
        if (newValue !== null) setText(newValue)
    }

    const handleEditClick = e => {
        e.stopPropagation() // Prevent selection when clicking edit
        if (isAuthoringDisabled) return // editing off in aggregate views
        setIsEditable(true)
        setTimeout(() => {
            textareaRef.current.focus()
        }, 0)
    }
    const handleKeyDown = async e => {
        if (e.key === 'Enter') {
            await executeStatementEditing()
        } else if (e.key === 'Escape') {
            // revert to inital values
            setText(initialText)
            setSelectedTags(tags)
            setIsEditable(false)
        }
    }
    const handleBlur = async e => {
        console.log('handleBlur')
        const tagComponent = tagComponentRef.current
        const textareaComponent = textareaRef.current
        // Is it clicking completely outside the textarea or tag component?
        if (
            (tagComponent && tagComponent.contains(e.relatedTarget)) ||
            (textareaComponent && textareaComponent.contains(e.relatedTarget))
        ) {
            return
        }
        await executeStatementEditing()
    }

    const executeStatementEditing = async () => {
        const hasTextChanged = text !== initialText
        const hasTagChanged =
            selectedTags.length !== tags.length ||
            selectedTags.some((tag, index) => tag !== tags[index])

        if (!hasTextChanged && !hasTagChanged) {
            setIsEditable(false)
            return
        }

        setIsLoading(true)
        if (
            window.myEventEmitter &&
            myEventEmitter._events['onStatementsAction']
        ) {
            myEventEmitter.emit('onStatementsAction', {
                action: 'edit_statement',
            })
        }

        try {
            const { uid: savedUid, entriesAndGraphOfContext } = await useZustand
                .getState()
                .editStatement(text, selectedTags, uid)

            // Text split into several statements gets new uids; the selection
            // Set would otherwise hold the dead one.
            if (savedUid !== uid) setSelectedStatements(new Set())

            await recalculateCurrentGraph({
                precomputedGraphData: { entriesAndGraphOfContext },
            })
        } finally {
            setIsLoading(false)
            setIsEditable(false)
        }
    }

    const renderTextWithHighlight = () => {
        if (!highlightedWords || highlightedWords.length === 0) return text

        const regex = new RegExp(
            `(${highlightedWords
                .map(word => {
                    // Escape special regex characters first
                    const escaped = word.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
                    // Convert the escaped string to allow either space or underscore
                    return escaped.replace(/[ _]/g, '[ _]')
                })
                .join('|')})`,
            'gi'
        )
        return text.split(regex).map((part, i) => {
            if (
                highlightedWords.some(
                    word =>
                        part.toLowerCase().replace(/[ _]/g, ' ') ===
                        word.toLowerCase().replace(/[ _]/g, ' ')
                )
            ) {
                return <mark key={i}>{part}</mark>
            }
            return part
        })
    }
    const handleCopyClick = async e => {
        e.stopPropagation()
        showSnackbar('success', `Copied to clipboard!`)
        await navigator.clipboard.writeText(text)

        sendToAnalytics({
            event: 'statements_action',
            data: {
                userId: username,
                action: 'copy_statement',
                source: 'statement',
            },
        })

        if (typeof posthog != 'undefined') {
            posthog.capture('statements_action', {
                action: 'copy_statement',
            })
        }
    }

    const handleLinkClick = async e => {
        e.stopPropagation()

        if (linkInText) {
            window.open(linkInText, '_blank')
        }

        sendToAnalytics({
            event: 'statements_action',
            data: {
                userId: username,
                action: 'open_link',
                source: 'statement',
            },
        })

        if (typeof posthog != 'undefined') {
            posthog.capture('statements_action', {
                action: 'open_link',
            })
        }
    }

    const isInteracting = isSelected || isHovered
    let rowBackground = TRANSPARENT_BG
    if (isInteracting) rowBackground = SELECTION_BG
    else if (isActiveMatch) rowBackground = activeMatchBg

    const isThereTags = tags && tags.length > 0
    const chipList = useMemo(
        () =>
            [...new Set(selectedTags)].map(tag => (
                <Chip
                    key={`${uid}-${tag}`}
                    label={tag}
                    size="small"
                    sx={{
                        color: theme => theme.palette.colors.chip,
                        height: '20px',
                        fontSize: '12px',
                    }}
                />
            )),
        [tags, selectedTags]
    )

    return (
        <div
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            onClick={e => {
                onSelect(uid)
            }}
            style={{
                position: 'relative',
                padding: '0px 10px 1px 4px',
                marginBottom: '8px',
                borderRadius: '4px',
                font: "14px 'IstokWeb-Regular',sans-serif",
                backgroundColor: rowBackground,
                transition: 'background-color 0.35s ease-out',
                cursor: 'pointer',
                opacity: grayedOut ? 0.5 : 1,
            }}
        >
            {isEditable ? (
                <TextareaAutosize
                    ref={textareaRef}
                    value={text}
                    onChange={e => setText(e.target.value)}
                    onSelect={handleSelect}
                    onBlur={handleBlur}
                    onKeyDown={handleKeyDown}
                    style={{
                        font: "14px 'IstokWeb-Regular',sans-serif",
                        width: '100%',
                        borderTop: 'none',
                        borderLeft: 'none',
                        borderRight: 'none',
                        borderRadius: '0px',
                        boxShadow: 'none',
                        outline: 'none',
                        resize: 'none',
                        lineHeight: '1.5',
                        backgroundColor: 'transparent',
                        padding: '4px',
                        margin: '0',
                        marginBottom: isThereTags ? '7px' : '0',
                    }}
                    disabled={isLoading}
                />
            ) : (
                <>
                    <div
                        style={{
                            font: "14px 'IstokWeb-Regular',sans-serif",
                            lineHeight: '1.5',
                            padding: '4px',
                            color: theme => theme.palette.background.text,
                            marginBottom: 0,
                        }}
                    >
                        {renderTextWithHighlight()}
                    </div>
                </>
            )}
            {(isThereTags || isEditable) && (
                <Box
                    ref={tagComponentRef}
                    onBlur={handleBlur}
                    onKeyDown={handleKeyDown}
                    sx={{
                        display: 'flex',
                        gap: 0.5,
                        flexWrap: 'wrap',
                        marginTop: '4px',
                        marginBottom: '8px',
                    }}
                >
                    {isEditable && (
                        <Tags
                            onTagsChange={setSelectedTags}
                            selectedTags={selectedTags}
                            noLabel={true}
                        />
                    )}
                    {isThereTags && !isEditable && chipList}
                </Box>
            )}
            {contextLabel && (
                <Box
                    sx={{
                        display: 'flex',
                        gap: 0.5,
                        flexWrap: 'wrap',
                        flexDirection: 'row-reverse',
                        marginTop: '4px',
                        marginBottom: '8px',
                    }}
                >
                    <Chip
                        label={contextLabel}
                        size="small"
                        color="primary"
                        variant="outlined"
                        sx={{
                            height: '20px',
                            fontSize: '12px',
                        }}
                    />
                </Box>
            )}
            <Fade in={isHovered && !isEditable} timeout={300}>
                <Box
                    sx={{
                        position: 'absolute',
                        right: '0px',
                        top: '0px',
                        display: 'flex',
                        flexDirection: 'column-reverse',
                        gap: '4px',
                        backgroundColor: theme =>
                            theme.palette.background.dialogs,
                        opacity: 0.5,
                        padding: '4px',
                        borderRadius: '4px',
                        border: 'none',
                    }}
                >
                    {linkInText && (
                        <IconButton
                            size="small"
                            onClick={handleLinkClick}
                            sx={{
                                padding: '4px',
                            }}
                        >
                            <LinkIcon fontSize="small" />
                        </IconButton>
                    )}
                    <IconButton
                        size="small"
                        onClick={handleCopyClick}
                        sx={{
                            padding: '4px',
                        }}
                    >
                        <ContentCopyIcon fontSize="small" />
                    </IconButton>
                    {!isAuthoringDisabled && (
                        <IconButton
                            size="small"
                            aria-label="Edit statement"
                            onClick={handleEditClick}
                            sx={{
                                padding: '4px',
                            }}
                        >
                            <EditIcon fontSize="small" />
                        </IconButton>
                    )}
                </Box>
            </Fade>
        </div>
    )
}

export default Statement
